import AddIcon from "@mui/icons-material/Add";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import CustomerFinanceApi from "../../../../infrastructure/api/customerFinance";
import PaymentGatewayApi from "../../../../infrastructure/api/paymentGateway";
import RequestPaymentActionCell from "../../../components/payment-gateway/RequestPaymentActionCell";
import {
  formatRial,
  normalizeRequestsPaymentSummary,
  pick,
} from "../../../components/payment-gateway/paymentGatewayUtils";

const CARD_SX = { borderRadius: 2, boxShadow: 1 };
const PAGE_BG = "#F6F8FB";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("fa-IR");
};

const normalizePendingSignatures = (rawList) => {
  if (!Array.isArray(rawList)) return [];
  return rawList
    .map((item) => {
      const requestId = Number(pick(item, "requestId", "RequestId") || 0);
      if (!requestId) return null;
      const actionUrl =
        pick(item, "actionUrl", "ActionUrl") ||
        `/SLN/Customer/ApplicantContractSign/${requestId}`;
      return {
        requestId,
        referenceId: pick(item, "referenceId", "ReferenceId"),
        contractId: pick(item, "contractId", "ContractId"),
        contractNumber: pick(item, "contractNumber", "ContractNumber") || "",
        requestTitle: pick(item, "requestTitle", "RequestTitle") || "",
        statusTitle: pick(item, "statusTitle", "StatusTitle") || "",
        actionUrl,
      };
    })
    .filter(Boolean);
};

const SummaryRow = ({ label, value, highlight = false }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography
      variant="body1"
      fontWeight={700}
      color={highlight ? "primary.main" : "text.primary"}
      sx={{ mt: 0.25 }}
    >
      {value}
    </Typography>
  </Box>
);

const SLNCustomerPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState([]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewRes, paymentRes] = await Promise.all([
        CustomerFinanceApi.getDashboardOverview(),
        PaymentGatewayApi.getRequestsPaymentSummary(),
      ]);

      const { error: overviewError, data } =
        CustomerFinanceApi.normalizeDashboardOverview(overviewRes);
      const { error: paymentError, data: paymentData } =
        normalizeRequestsPaymentSummary(paymentRes);

      if (overviewError) {
        setError(overviewError);
        setOverview(null);
      } else {
        setOverview(data);
      }

      if (!paymentError) {
        setPaymentSummary(paymentData);
      }
    } catch {
      setError("بارگذاری داشبورد ناموفق بود.");
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const requests = pick(overview, "requests", "Requests") ?? {};
  const finance = pick(overview, "finance", "Finance") ?? {};
  const nextAction = pick(overview, "nextAction", "NextAction") ?? null;
  const pendingSignatures = normalizePendingSignatures(
    pick(overview, "pendingContractSignatures", "PendingContractSignatures")
  );

  const activeCount = Number(pick(requests, "activeCount", "ActiveCount") ?? 0);
  const latestRequests = pick(requests, "latest", "Latest") ?? [];
  const nextActionType = pick(nextAction, "nextActionType", "NextActionType");
  const nextActionTitle =
    pick(nextAction, "title", "Title") || "ثبت درخواست نیروگاه";
  const nextActionDescription =
    pick(nextAction, "description", "Description") ||
    "برای شروع فرآیند، درخواست احداث نیروگاه خورشیدی خود را ثبت کنید.";
  const nextActionUrl = pick(nextAction, "actionUrl", "ActionUrl") || "";
  const showSubmitRequestCta =
    activeCount === 0 || nextActionType === "SubmitNewRequest";
  const showSignContractCta =
    nextActionType === "SignApplicantContract" && pendingSignatures.length === 0;

  const unpaidCount = Number(
    pick(finance, "unpaidInvoiceCount", "UnpaidInvoiceCount") ?? 0
  );
  const totalUnpaid = pick(finance, "totalUnpaidAmount", "TotalUnpaidAmount");
  const availableBalance = pick(finance, "availableBalance", "AvailableBalance");
  const latestInvoiceId = pick(finance, "latestInvoiceId", "LatestInvoiceId");

  if (loading) {
    return (
      <Box sx={{ bgcolor: PAGE_BG, minHeight: "100%" }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 1.5, md: 2 }, py: 2 }}>
          <Skeleton variant="text" width={200} height={36} sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }

  return (
    <Box data-testid="customer-dashboard" sx={{ bgcolor: PAGE_BG, minHeight: "100%" }}>
      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 1.5, md: 2 }, py: { xs: 1.5, md: 2 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight={700}>
            داشبورد متقاضی
          </Typography>
          <Button
            component={Link}
            to="/SLN/Customer/Requests/create"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            sx={{ borderRadius: 2 }}
          >
            درخواست جدید
          </Button>
        </Box>

        {error ? (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={loadDashboard}>
                تلاش مجدد
              </Button>
            }
          >
            {error}
          </Alert>
        ) : null}

        {pendingSignatures.length > 0 ? (
          <Alert
            severity="info"
            icon={<BorderColorOutlinedIcon fontSize="inherit" />}
            data-testid="pending-contract-signatures"
            sx={{
              mb: 2,
              border: "1px solid",
              borderColor: "info.light",
              "& .MuiAlert-message": { width: "100%", py: 0.5 },
            }}
          >
            <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.4 }}>
              قرارداد آماده امضا
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, mb: 1.25 }}>
              برای ادامه پرونده، قرارداد تولیدشده را مشاهده و به‌صورت دیجیتال امضا کنید.
            </Typography>
            <Stack spacing={1}>
              {pendingSignatures.map((item) => (
                <Stack
                  key={`${item.requestId}-${item.contractId || "c"}`}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {item.requestTitle || `درخواست ${item.referenceId || item.requestId}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.contractNumber
                        ? `شماره قرارداد: ${item.contractNumber}`
                        : `شناسه درخواست: ${item.referenceId || item.requestId}`}
                      {item.statusTitle ? ` — ${item.statusTitle}` : ""}
                    </Typography>
                  </Box>
                  <Button
                    component={Link}
                    to={item.actionUrl}
                    variant="contained"
                    size="small"
                    startIcon={<BorderColorOutlinedIcon />}
                    sx={{ flexShrink: 0 }}
                  >
                    امضای قرارداد
                  </Button>
                </Stack>
              ))}
            </Stack>
          </Alert>
        ) : null}

        {showSubmitRequestCta ? (
          <Alert
            severity="warning"
            variant="filled"
            icon={false}
            sx={{
              mb: 2,
              bgcolor: "warning.main",
              color: "#1A1A1A",
              border: "1px solid",
              borderColor: "warning.dark",
              boxShadow: "0 2px 10px rgba(246, 174, 45, 0.45)",
              "& .MuiAlert-message": { py: 0.5 },
            }}
          >
            <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.4 }}>
              {nextActionTitle}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600, opacity: 0.92 }}>
              {nextActionDescription}
            </Typography>
          </Alert>
        ) : null}

        {showSignContractCta ? (
          <Alert
            severity="info"
            sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}
            action={
              nextActionUrl ? (
                <Button
                  color="inherit"
                  size="small"
                  component={Link}
                  to={nextActionUrl}
                  startIcon={<BorderColorOutlinedIcon />}
                >
                  امضای قرارداد
                </Button>
              ) : null
            }
          >
            <Typography variant="subtitle2" fontWeight={700}>
              {nextActionTitle}
            </Typography>
            <Typography variant="body2">{nextActionDescription}</Typography>
          </Alert>
        ) : null}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ ...CARD_SX, height: "100%" }}>
              <CardContent sx={{ py: 2, px: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <AssignmentOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    درخواست‌ها
                  </Typography>
                </Stack>

                {activeCount === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    درخواست فعالی ثبت نشده است.
                  </Typography>
                ) : (
                  <Stack spacing={1.5} sx={{ mb: 1.5 }}>
                    <SummaryRow
                      label="تعداد درخواست‌های فعال"
                      value={activeCount.toLocaleString("fa-IR")}
                    />
                    {latestRequests[0] ? (
                      <Box sx={{ p: 1.25, bgcolor: "grey.50", borderRadius: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          آخرین وضعیت
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                          {pick(latestRequests[0], "title", "Title") ||
                            `درخواست ${pick(latestRequests[0], "referenceId", "ReferenceId")}`}
                        </Typography>
                        <Chip
                          size="small"
                          label={pick(latestRequests[0], "statusTitle", "StatusTitle") || "—"}
                          sx={{ mt: 0.75, height: 22, fontSize: 11 }}
                        />
                      </Box>
                    ) : null}
                  </Stack>
                )}

                <Button component={Link} to="/SLN/Customer/Requests" size="small" variant="text">
                  مشاهده درخواست‌ها
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ ...CARD_SX, height: "100%" }}>
              <CardContent sx={{ py: 2, px: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <ReceiptLongOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    خلاصه مالی
                  </Typography>
                </Stack>

                <Stack direction="row" flexWrap="wrap" sx={{ mb: 1.5, gap: 2 }}>
                  <SummaryRow
                    label="فاکتورهای پرداخت‌نشده"
                    value={unpaidCount.toLocaleString("fa-IR")}
                  />
                  <SummaryRow
                    label="مبلغ قابل پرداخت"
                    value={formatRial(totalUnpaid)}
                    highlight={unpaidCount > 0}
                  />
                  <SummaryRow label="اعتبار پنل" value={formatRial(availableBalance)} />
                </Stack>

                <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
                  {unpaidCount > 0 ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        if (latestInvoiceId) {
                          navigate(`/SLN/Customer/InvoicePayment/${latestInvoiceId}`);
                        } else {
                          navigate("/SLN/Customer/Requests");
                        }
                      }}
                    >
                      پرداخت
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => navigate("/SLN/Customer/Balance/TopUp")}
                  >
                    افزایش اعتبار
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ ...CARD_SX }}>
          <CardContent sx={{ py: 2, px: { xs: 1.5, sm: 2 } }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              درخواست‌های اخیر
            </Typography>

            {latestRequests.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                هنوز درخواستی ثبت نکرده‌اید.
              </Typography>
            ) : isMobile ? (
              <Stack spacing={1}>
                {latestRequests.map((req) => {
                  const id = pick(req, "id", "Id");
                  return (
                    <Card
                      key={id}
                      variant="outlined"
                      data-testid="request-card"
                      sx={{ borderRadius: 1.5, cursor: "pointer" }}
                      onClick={() => navigate(`/SLN/Customer/Requests/${id}`)}
                    >
                      <CardContent sx={{ py: 1.25, px: 1.5, "&:last-child": { pb: 1.25 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              data-testid="request-reference"
                            >
                              #{pick(req, "referenceId", "ReferenceId") || id}
                            </Typography>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {pick(req, "title", "Title") || "—"}
                            </Typography>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
                              <Chip
                                label={pick(req, "statusTitle", "StatusTitle") || "—"}
                                size="small"
                                sx={{ height: 22, fontSize: 11 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(pick(req, "updateOn", "UpdateOn"))}
                              </Typography>
                            </Stack>
                          </Box>
                        </Stack>
                        <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
                          <RequestPaymentActionCell
                            row={req}
                            paymentSummary={paymentSummary}
                            compact={false}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#F3F6F9" }}>
                      {["شناسه", "عنوان", "وضعیت", "ایجاد", "بروزرسانی", ""].map((label) => (
                        <th
                          key={label || "action"}
                          style={{
                            padding: "6px 8px",
                            fontWeight: 600,
                            fontSize: 12,
                            textAlign: "right",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {latestRequests.map((req) => {
                      const id = pick(req, "id", "Id");
                      return (
                        <tr
                          key={id}
                          style={{ borderBottom: "1px solid #eee", cursor: "pointer" }}
                          onClick={() => navigate(`/SLN/Customer/Requests/${id}`)}
                        >
                          <td style={{ padding: "6px 8px", fontSize: 12 }}>
                            {pick(req, "referenceId", "ReferenceId") || id}
                          </td>
                          <td style={{ padding: "6px 8px", fontSize: 12, maxWidth: 180 }}>
                            <Typography variant="body2" noWrap fontSize={12}>
                              {pick(req, "title", "Title") || "—"}
                            </Typography>
                          </td>
                          <td style={{ padding: "6px 8px" }}>
                            <Chip
                              label={pick(req, "statusTitle", "StatusTitle") || "—"}
                              size="small"
                              sx={{ height: 22, fontSize: 11 }}
                            />
                          </td>
                          <td style={{ padding: "6px 8px", fontSize: 12, whiteSpace: "nowrap" }}>
                            {formatDate(pick(req, "createOn", "CreateOn"))}
                          </td>
                          <td style={{ padding: "6px 8px", fontSize: 12, whiteSpace: "nowrap" }}>
                            {formatDate(pick(req, "updateOn", "UpdateOn"))}
                          </td>
                          <td style={{ padding: "6px 8px" }} onClick={(e) => e.stopPropagation()}>
                            <RequestPaymentActionCell
                              row={req}
                              paymentSummary={paymentSummary}
                              compact
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default SLNCustomerPage;
